import React, { useEffect, useMemo, useState } from 'react';
import {
  HiPlus,
  HiPencil,
  HiTrash,
  HiX,
  HiOutlineCheckCircle,
  HiOutlineDownload,
  HiOutlineDocumentText,
  HiOutlineUpload,
} from 'react-icons/hi';
import toast from 'react-hot-toast';

import { docxTemplatesApi } from '../../services/api';

const DATA_SHAPES = [
  { value: 'ongc_weekly', label: 'ONGC Weekly Bill (per-day attendance per therapy)' },
  { value: 'ongc_session', label: 'ONGC Session Distribution (monthly counts)' },
  { value: 'ongc_general', label: 'ONGC General Receipt (combined therapy line)' },
  { value: 'prl_bill', label: 'PRL Bill (date-range invoice)' },
  { value: 'attendance_bifurcation', label: 'Attendance Bifurcation (day-by-day grid)' },
];

const DATA_SHAPE_LABELS = DATA_SHAPES.reduce((m, s) => {
  m[s.value] = s.label;
  return m;
}, {});

function formatBytes(bytes) {
  if (!bytes) return '-';
  const n = Number(bytes);
  if (Number.isNaN(n)) return '-';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

const slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50);

const DocxTemplates = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [editFile, setEditFile] = useState(null);
  const [busyRowId, setBusyRowId] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await docxTemplatesApi.getAll();
      setItems(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const row of items) {
      if (!map.has(row.template_code)) map.set(row.template_code, []);
      map.get(row.template_code).push(row);
    }
    return Array.from(map.entries()).map(([code, rows]) => ({
      template_code: code,
      data_shape: rows[0]?.data_shape,
      active: rows.find((r) => r.is_active === 1 || r.is_active === true) || null,
      versions: rows,
    }));
  }, [items]);

  const knownCodes = useMemo(() => grouped.map((g) => g.template_code), [grouped]);

  const handleActivate = async (row) => {
    if (row.is_active === 1 || row.is_active === true) return;
    setBusyRowId(row.id);
    try {
      await docxTemplatesApi.activate(row.id);
      toast.success(`Activated "${row.name}"`);
      await fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to activate');
    } finally {
      setBusyRowId(null);
    }
  };

  const handleDelete = async (row) => {
    if (row.is_system) {
      toast.error('System default templates cannot be deleted');
      return;
    }
    if (!window.confirm(`Delete "${row.name}"? This will remove the file from storage.`)) return;
    setBusyRowId(row.id);
    try {
      await docxTemplatesApi.remove(row.id);
      toast.success('Template deleted');
      await fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    } finally {
      setBusyRowId(null);
    }
  };

  const handleDownload = async (row) => {
    setBusyRowId(row.id);
    try {
      const res = await docxTemplatesApi.download(row.id);
      const blob = new Blob([res.data], {
        type:
          res.headers?.['content-type'] ||
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const baseName =
        row.original_filename ||
        `${row.template_code}_v${row.id}.docx`;
      link.download = baseName.endsWith('.docx') ? baseName : `${baseName}.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to download');
    } finally {
      setBusyRowId(null);
    }
  };

  const openEdit = (row) => {
    setEditing(row);
    setEditForm({ name: row.name || '', description: row.description || '' });
    setEditFile(null);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    try {
      const trimmedName = editForm.name.trim();
      const trimmedDesc = editForm.description.trim() || null;

      // If a new .docx is provided, upload it as a NEW version (auto-activates on server).
      // This avoids forcing a delete and matches the existing upload behavior.
      if (editFile) {
        if (editFile.size > 5 * 1024 * 1024) {
          toast.error('File exceeds 5 MB limit');
          return;
        }
        const fd = new FormData();
        fd.append('name', trimmedName);
        fd.append('description', trimmedDesc || '');
        fd.append('data_shape', editing.data_shape);
        fd.append('template_code', editing.template_code);
        fd.append('file', editFile);
        await docxTemplatesApi.create(fd);
        toast.success('New version uploaded & activated');
      } else {
        await docxTemplatesApi.update(editing.id, {
          name: trimmedName,
          description: trimmedDesc,
        });
        toast.success('Template updated');
      }

      setEditing(null);
      await fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">DOCX Templates</h1>
          <p className="text-gray-500 mt-1">
            Upload custom .docx templates and manage which version is used for patient exports.
          </p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <HiPlus className="w-5 h-5" />
          Upload Template
        </button>
      </div>

      {grouped.length === 0 ? (
        <div className="card text-center p-8 text-gray-500">No templates yet.</div>
      ) : (
        <div className="space-y-6">
          {grouped.map((group) => (
            <div key={group.template_code} className="card overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {group.active?.name || group.versions[0]?.name || group.template_code}
                    </h2>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-mono bg-gray-100 text-gray-600">
                      {group.template_code}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700">
                      {DATA_SHAPE_LABELS[group.data_shape] || group.data_shape}
                    </span>
                  </div>
                  {group.active?.description && (
                    <p className="text-sm text-gray-500 mt-1">{group.active.description}</p>
                  )}
                  <div className="text-xs text-gray-500 mt-2">
                    {group.active ? (
                      <span className="inline-flex items-center gap-1">
                        <HiOutlineCheckCircle className="w-4 h-4 text-emerald-500" />
                        Active version: <strong className="text-gray-700">{group.active.original_filename || (group.active.is_system ? 'System default' : `v${group.active.id}`)}</strong>
                      </span>
                    ) : (
                      <span className="text-rose-600">No active version</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[920px] text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left px-6 py-3 font-semibold text-gray-700">Name</th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-700">File</th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-700">Size</th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-700">Uploaded By</th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-700">Uploaded At</th>
                      <th className="text-center px-6 py-3 font-semibold text-gray-700">Status</th>
                      <th className="text-right px-6 py-3 font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.versions.map((row) => {
                      const isActive = row.is_active === 1 || row.is_active === true;
                      const isSystem = row.is_system === 1 || row.is_system === true;
                      return (
                        <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-6 py-3">
                            <div className="font-medium text-gray-900">{row.name}</div>
                            {row.description && (
                              <div className="text-xs text-gray-500 mt-0.5">{row.description}</div>
                            )}
                          </td>
                          <td className="px-6 py-3 text-gray-600">
                            {row.original_filename || (isSystem ? '— (built-in)' : '—')}
                          </td>
                          <td className="px-6 py-3 text-gray-600">{formatBytes(row.file_size)}</td>
                          <td className="px-6 py-3 text-gray-600">
                            {row.uploaded_by_name || (isSystem ? 'System' : '—')}
                          </td>
                          <td className="px-6 py-3 text-gray-600">{row.created_at}</td>
                          <td className="px-6 py-3 text-center">
                            <div className="flex flex-wrap gap-1 justify-center">
                              {isSystem && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-200 text-gray-700">
                                  Default
                                </span>
                              )}
                              {isActive ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700">
                                  <HiOutlineCheckCircle className="w-3 h-3" /> Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                                  Inactive
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-3">
                            <div className="flex items-center justify-end gap-1">
                              {!isActive && (
                                <button
                                  onClick={() => handleActivate(row)}
                                  disabled={busyRowId === row.id}
                                  className="px-2 py-1 text-xs rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                                  title="Make this version active"
                                >
                                  Activate
                                </button>
                              )}
                              <button
                                onClick={() => openEdit(row)}
                                className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                                title="Edit name / description"
                              >
                                <HiPencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDownload(row)}
                                disabled={busyRowId === row.id}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50"
                                title="Download .docx"
                              >
                                <HiOutlineDownload className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(row)}
                                disabled={isSystem || busyRowId === row.id}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-30 disabled:hover:bg-transparent"
                                title={isSystem ? 'System defaults cannot be deleted' : 'Delete version'}
                              >
                                <HiTrash className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {showUpload && (
        <UploadTemplateModal
          onClose={() => setShowUpload(false)}
          onUploaded={async () => {
            setShowUpload(false);
            await fetchItems();
          }}
          knownCodes={knownCodes}
          knownGroups={grouped}
        />
      )}

      {editing && (
        <div className="modal-shell flex items-start sm:items-center justify-center overflow-y-auto p-4">
          <div className="modal-panel max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Edit Template Version</h3>
              <button
                onClick={() => setEditing(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={submitEdit} className="p-6 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows={3}
                  value={editForm.description}
                  onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Replace file (optional)
                </label>
                <input
                  type="file"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => setEditFile(e.target.files?.[0] || null)}
                  className="w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  If you select a file, it will be uploaded as a <strong>new version</strong> and set active automatically. No need to delete.
                </p>
              </div>
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="w-full sm:w-auto px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const UploadTemplateModal = ({ onClose, onUploaded, knownCodes, knownGroups }) => {
  const [mode, setMode] = useState('new'); // 'new' or 'version'
  const [existingCode, setExistingCode] = useState(knownCodes[0] || '');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dataShape, setDataShape] = useState(DATA_SHAPES[0].value);
  const [templateCode, setTemplateCode] = useState('');
  const [touchedCode, setTouchedCode] = useState(false);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverErrors, setServerErrors] = useState([]);

  const [placeholderRef, setPlaceholderRef] = useState(null);
  const [showPlaceholders, setShowPlaceholders] = useState(true);

  const effectiveDataShape = mode === 'version'
    ? (knownGroups.find((g) => g.template_code === existingCode)?.data_shape || dataShape)
    : dataShape;

  useEffect(() => {
    let cancelled = false;
    if (!effectiveDataShape) return;
    docxTemplatesApi
      .getPlaceholders(effectiveDataShape)
      .then((res) => {
        if (!cancelled) setPlaceholderRef(res.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setPlaceholderRef(null);
      });
    return () => { cancelled = true; };
  }, [effectiveDataShape]);

  useEffect(() => {
    if (mode === 'new' && !touchedCode) {
      setTemplateCode(slugify(name));
    }
  }, [name, mode, touchedCode]);

  const submit = async (e) => {
    e.preventDefault();
    setServerErrors([]);
    if (!file) {
      toast.error('Please select a .docx file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File exceeds 5 MB limit');
      return;
    }

    const fd = new FormData();
    fd.append('name', name.trim());
    fd.append('description', description.trim());
    fd.append('data_shape', effectiveDataShape);
    fd.append('template_code', mode === 'version' ? existingCode : (templateCode || slugify(name)));
    fd.append('file', file);

    setSubmitting(true);
    try {
      await docxTemplatesApi.create(fd);
      toast.success('Template uploaded');
      onUploaded();
    } catch (err) {
      const data = err.response?.data;
      if (Array.isArray(data?.errors) && data.errors.length > 0) {
        setServerErrors(data.errors);
      }
      toast.error(data?.message || 'Failed to upload template');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-shell flex items-start sm:items-center justify-center overflow-y-auto p-4">
      <div className="modal-panel max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <HiOutlineUpload className="w-5 h-5 text-primary-600" />
            Upload DOCX Template
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <HiX className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Upload mode</label>
            <div className="flex flex-col md:flex-row gap-3">
              <button
                type="button"
                onClick={() => setMode('new')}
                className={`flex-1 px-4 py-3 rounded-lg border text-left ${
                  mode === 'new'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="font-semibold">New template</div>
                <div className="text-xs mt-1 opacity-80">Creates a brand-new template_code (e.g. "abc_lab_bill") that reuses an existing data shape.</div>
              </button>
              <button
                type="button"
                onClick={() => setMode('version')}
                disabled={knownCodes.length === 0}
                className={`flex-1 px-4 py-3 rounded-lg border text-left ${
                  mode === 'version'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40'
                }`}
              >
                <div className="font-semibold">New version of existing</div>
                <div className="text-xs mt-1 opacity-80">Replaces the active version under an existing template_code.</div>
              </button>
            </div>
          </div>

          {mode === 'version' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template <span className="text-red-500">*</span>
              </label>
              <select
                value={existingCode}
                onChange={(e) => setExistingCode(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {knownGroups.map((g) => (
                  <option key={g.template_code} value={g.template_code}>
                    {g.active?.name || g.template_code} ({g.template_code} — {g.data_shape})
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data shape <span className="text-red-500">*</span>
              </label>
              <select
                value={dataShape}
                onChange={(e) => setDataShape(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {DATA_SHAPES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                The data shape determines which fields the export controller fills in.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. ONGC Weekly Bill v2"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            {mode === 'new' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={templateCode}
                  onChange={(e) => {
                    setTouchedCode(true);
                    setTemplateCode(slugify(e.target.value));
                  }}
                  placeholder="lowercase_with_underscores"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">Auto-suggested from the name. Must be unique.</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              .docx file <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              required
              className="w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
            />
            <p className="text-xs text-gray-500 mt-1">Max 5 MB. Word documents only.</p>
          </div>

          {placeholderRef && (
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => setShowPlaceholders((s) => !s)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <span className="flex items-center gap-2">
                  <HiOutlineDocumentText className="w-4 h-4" />
                  Required placeholders for this data shape
                </span>
                <span className="text-xs text-gray-500">
                  {showPlaceholders ? 'Hide' : 'Show'}
                </span>
              </button>
              {showPlaceholders && (
                <div className="px-4 pb-4 text-sm text-gray-700 space-y-3">
                  {placeholderRef.description && (
                    <p className="text-gray-500">{placeholderRef.description}</p>
                  )}
                  <div>
                    <div className="text-xs font-semibold text-gray-700 uppercase mb-1">Static fields</div>
                    <div className="flex flex-wrap gap-1">
                      {(placeholderRef.static || []).map((tag) => (
                        <code key={tag} className="px-2 py-0.5 bg-gray-100 rounded text-xs font-mono">{tag}</code>
                      ))}
                    </div>
                  </div>
                  {placeholderRef.loop && (
                    <div>
                      <div className="text-xs font-semibold text-gray-700 uppercase mb-1">Loop block</div>
                      <code className="px-2 py-0.5 bg-amber-50 text-amber-800 rounded text-xs font-mono">{placeholderRef.loop}</code>
                      {(placeholderRef.itemFields || []).length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs text-gray-500 mb-1">Inside the loop, use:</div>
                          <div className="flex flex-wrap gap-1">
                            {(placeholderRef.itemFields || []).map((tag) => (
                              <code key={tag} className="px-2 py-0.5 bg-gray-100 rounded text-xs font-mono">{tag}</code>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Tip: type the placeholders as a single run in Word — avoid styling parts of a tag (bold/italic on partial characters splits the run and breaks templating).
                  </p>
                </div>
              )}
            </div>
          )}

          {serverErrors.length > 0 && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
              <div className="text-sm font-semibold text-rose-700 mb-1">
                Template validation failed
              </div>
              <ul className="list-disc list-inside text-sm text-rose-700 space-y-1">
                {serverErrors.map((msg, idx) => (
                  <li key={idx}>{msg}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting ? 'Uploading...' : 'Upload & Activate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocxTemplates;
